import React, { useState } from "react";
import { injectIntl } from "react-intl";
import cx from 'classnames';
import moment from 'moment';
import withStyles from "isomorphic-style-loader/withStyles";
import { useMutation } from "@apollo/client";
import { Container } from "react-bootstrap";

import useQueryResponse from "../../lib/hooks/useQueryResponse";
import showToaster from "../../helpers/toastrMessgaes/showToaster";
import { getNotifications, markNotificationRead } from "../../lib/graphql";
import messages from "../../locale/messages";
import bt from '../../components/commonStyle.css';
import s from "./Notifications.css";

const PAGE_SIZE = 50;

const Notifications = (props) => {
    const { intl: { formatMessage } } = props;

    const [limit, setLimit] = useState(PAGE_SIZE);
    const [expandedId, setExpandedId] = useState(null);

    const { data, loading, refetch } = useQueryResponse({
        query: getNotifications,
        variables: { limit, offset: 0 },
        ssr: true,
        fetchPolicy: 'network-only'
    });

    const [markRead] = useMutation(markNotificationRead);

    const payload = data?.getNotifications;
    const results = payload?.result || [];
    const unreadCount = payload?.unreadCount || 0;
    const totalCount = payload?.totalCount || 0;
    const hasMore = results.length < totalCount;

    // Open a notification: expand to show the full body, and mark THAT one read (if unread).
    const handleOpen = async (item) => {
        const isOpening = expandedId !== item?.id;
        setExpandedId(isOpening ? item?.id : null);

        if (isOpening && item?.isRead !== true) {
            try {
                await markRead({ variables: { notificationId: Number(item?.id) } });
                await refetch();
            } catch (error) {
                showToaster({ messageId: 'errorMessage', requestContent: error?.message, toasterType: 'error' });
            }
        }
    };

    const handleMarkAllRead = async () => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            showToaster({ messageId: 'networkFailed', toasterType: 'error' });
            return;
        }
        try {
            const { data: resp } = await markRead({ variables: { markAll: true } });
            if (resp?.markNotificationRead?.status === 200) {
                await refetch();
            } else {
                showToaster({ messageId: 'errorMessage', requestContent: resp?.markNotificationRead?.errorMessage, toasterType: 'error' });
            }
        } catch (error) {
            showToaster({ messageId: 'errorMessage', requestContent: error?.message, toasterType: 'error' });
        }
    };

    const handleLoadMore = () => {
        setLimit((prev) => prev + PAGE_SIZE);
    };

    return (
        <div className={cx('subPageContainer')}>
            <Container>
                <div className='subPageWrapper'>
                    <div className={s.headerRow}>
                        <p className={s.pageTitle}>{formatMessage(messages.notifications)}</p>
                        {unreadCount > 0 && (
                            <button type="button" className={cx(bt.btnPrimary, s.markAllBtn)} onClick={handleMarkAllRead}>
                                {formatMessage(messages.markAllRead)}
                            </button>
                        )}
                    </div>

                    {loading && results.length === 0 ? (
                        <p className={s.emptyText}>{formatMessage(messages.loadingText)}</p>
                    ) : results.length > 0 ? (
                        <>
                            <ul className={s.notificationList}>
                                {results.map((item) => {
                                    const unread = item?.isRead !== true;
                                    const expanded = expandedId === item?.id;
                                    return (
                                        <li
                                            key={item?.id}
                                            className={cx(s.notificationItem, { [s.notificationItemUnread]: unread })}
                                            onClick={() => handleOpen(item)}
                                        >
                                            <div className={cx(s.iconCircle, { [s.iconCircleUnread]: unread })}>
                                                <span aria-hidden="true">&#9679;</span>
                                            </div>
                                            <div className={s.notificationBody}>
                                                <p className={cx(s.notificationTitle, { [s.notificationTitleUnread]: unread })}>
                                                    {item?.title}
                                                </p>
                                                <p className={expanded ? s.notificationFull : s.notificationPreview}>
                                                    {item?.body}
                                                </p>
                                                {item?.createdAt && (
                                                    <span className={s.notificationTime}>
                                                        {moment(item.createdAt).fromNow()}
                                                    </span>
                                                )}
                                            </div>
                                            {unread && <span className={s.unreadDot} />}
                                        </li>
                                    );
                                })}
                            </ul>

                            {hasMore && (
                                <div className={s.loadMoreWrap}>
                                    <button type="button" className={cx(bt.btnSecondary)} onClick={handleLoadMore} disabled={loading}>
                                        {formatMessage(messages.loadMore)}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className={s.emptyText}>{formatMessage(messages.noNotifications)}</p>
                    )}
                </div>
            </Container>
        </div>
    );
};

export default injectIntl(withStyles(bt, s)(Notifications));
